'use client';

import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import Layout from '../Layout';

// Tab Components
import ProjectOverview from './ProjectOverview';
import ProjectMeasurements from './ProjectMeasurements';
import DetailsTab from './DetailsTab';
import ProjectFiles from './ProjectFiles';
import ExportHistory from './ExportHistory';
import ProjectAnalytics from './ProjectAnalytics';
import DensityReportGenerator from '../../components/DensityReportGenerator';

// Modals
import ReportBuilder from '../../utils/ReportBuilder';

// Icons
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

// Utils
import { downloadDensityReport } from '../../utils/densityReportUtils';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function MappedProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Core project data
  const [projectData, setProjectData] = useState(null);
  const [memberIds, setMemberIds] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [projectPolygon, setProjectPolygon] = useState([]);
  
  // UI state
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(true);
  const [measurements, setMeasurements] = useState([]); // <-- ADD THIS
  const [measurementsLoading, setMeasurementsLoading] = useState(true);
  
  // Modal states
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  
  // Integration states
  const [availableIntegrations, setAvailableIntegrations] = useState({});
  
  // Report generation
  const [reportGenerating, setReportGenerating] = useState(false);

  // Fetch organization member IDs
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user) return;

      try {
        let tempMemberIds = [user.id];
        
        const { data: orgMember, error: orgError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (orgError && orgError.code !== 'PGRST116') {
          console.error('Error fetching organization membership:', orgError);
          setLoading(false);
          return;
        }

        if (orgMember) {
          setOrganizationId(orgMember.organization_id);
          
          const { data: orgMembers, error: orgMembersError } = await supabase
            .from('organization_members')
            .select('user_id')
            .eq('organization_id', orgMember.organization_id);

          if (orgMembersError) {
            console.error('Error fetching organization members:', orgMembersError);
            setLoading(false);
            return;
          }

          tempMemberIds = orgMembers.map((member) => member.user_id);
        }

        setMemberIds(tempMemberIds);
      } catch (err) {
        console.error('Unexpected error fetching initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [user]);

  // Fetch project data
  const fetchProjectData = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const { data: projectData, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) {
        console.error('Error fetching project data:', error);
      } else {
        setProjectData(projectData);
      }
    } catch (err) {
      console.error('Unexpected error fetching project data:', err);
    }
  }, [projectId]);

  // Fetch project polygon
  useEffect(() => {
    const fetchProjectPolygon = async () => {
      if (!projectId || memberIds.length === 0) return;
      
      try {
        const { data, error } = await supabase
          .from('measurements_with_details')
          .select('project_geofence')
          .in('profile_id', memberIds)
          .eq('project_id', projectId)
          .not('project_geofence', 'is', null)
          .limit(1)
          .single();

        if (error || !data?.project_geofence) {
          setProjectPolygon([]);
          return;
        }

        const geoJson = typeof data.project_geofence === 'string' 
          ? JSON.parse(data.project_geofence) 
          : data.project_geofence;

        if (geoJson && geoJson.coordinates && geoJson.coordinates.length > 0) {
          const coords = geoJson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
          setProjectPolygon(coords);
        } else {
          setProjectPolygon([]);
        }
      } catch (err) {
        console.error('Error fetching project polygon:', err);
        setProjectPolygon([]);
      }
    };

    fetchProjectPolygon();
  }, [projectId, memberIds]);

  // Fetch organization integrations
  useEffect(() => {
    const fetchOrganizationIntegrations = async () => {
      if (!organizationId) return;
      
      try {
        const { data, error } = await supabase
          .from('organization_integrations')
          .select('integration_name')
          .eq('organization_id', organizationId);
        
        if (error) {
          console.error('Error fetching organization integrations:', error);
          return;
        }
        
        const integrationMap = {};
        data.forEach(item => {
          integrationMap[item.integration_name] = true;
        });
        
        setAvailableIntegrations(integrationMap);
      } catch (err) {
        console.error('Unexpected error fetching organization integrations:', err);
      }
    };
    
    fetchOrganizationIntegrations();
  }, [organizationId]);

  // Fetch project data when component mounts
  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  useEffect(() => {
    const fetchMeasurements = async () => {
      if (!projectId) return;

      setMeasurementsLoading(true);
      try {
        const { data, error } = await supabase
          .from('measurements_with_details')
          .select('*') // Ensure all necessary columns are selected
          .eq('project_id', projectId)
          .order('LocalTimeOfMeasurement', { ascending: false }); // Optional: order the data

        if (error) {
          console.error('Error fetching project measurements:', error);
          setMeasurements([]);
        } else {
          setMeasurements(data || []);
        }
      } catch (err) {
        console.error('Unexpected error fetching measurements:', err);
        setMeasurements([]);
      } finally {
        setMeasurementsLoading(false);
      }
    };

    fetchMeasurements();
  }, [projectId]);

  // Set document title
  useEffect(() => {
    document.title = `Project | TransTech Connect`;
    return () => {
      document.title = "TransTech Connect";
    };
  }, []);

  // Export to CSV helper
  const exportToCSV = (data, filename = "export.csv") => {
    if (!data || data.length === 0) {
      console.error("No data available to export.");
      return;
    }

    const columns = {
      "first_name": "First Name",
      "last_name": "Last Name",
      "operator_name": "Operator",
      "project_display_name": "Project",
      "Density": "Density",
      "Compaction": "Compaction",
      "SurfaceTemp": "Surface Temperature",
      "LocalTimeOfMeasurement": "Local Time",
      "latitude": "Latitude",
      "longitude": "Longitude",
      "Description1": "Description 1",
      "Description2": "Description 2",
      "Station": "Station"
    };

    const headers = Object.values(columns);
    const rows = data.map((row) =>
      Object.keys(columns).map((key) => {
        let value = row[key];
        if (typeof value === "string") {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value !== null && value !== undefined ? value : "";
      }).join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Report generation handler
  const handleGenerateReport = async (projectData, measurements, filename, options) => {
    setReportGenerating(true);
    try {
      await downloadDensityReport(projectData, measurements, filename, options);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setReportGenerating(false);
    }
  };

  // Prepare project context for child components
  const projectContext = {
    projectId,
    projectData,
    memberIds,
    organizationId,
    projectPolygon,
    availableIntegrations,
    user,
    onProjectDataUpdate: fetchProjectData,
    onExportCSV: exportToCSV
  };

  if (loading || !user || measurementsLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 border-4 border-gray-300 border-t-yellow-500 rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-600">Loading your project...</p>
            </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Modern Header */}
      <div className="bg-white rounded-md border border-gray-200">
        <div className="px-4">
          {/* Top Navigation Bar */}
          <div className="flex items-center justify-between h-16">
            {/* Left side - Breadcrumb navigation */}
            <div className="flex items-center">
              <nav className="flex" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-2">
                  <li className="flex items-center">
                    <button
                      onClick={() => navigate('/projects')}
                      className="ml-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Projects
                    </button>
                  </li>
                  <li className="flex items-center">
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                    <span className="ml-2 text-sm font-medium text-gray-900">
                      {projectData?.name || 'Loading...'}
                    </span>
                  </li>
                </ol>
              </nav>
            </div>

            {/* Right side - Actions */}
            {/* <div className="flex items-center space-x-4">
              <Menu as="div" className="relative">
                <MenuButton className="inline-flex items-center gap-x-1 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                  <span>Actions</span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </MenuButton>
                <MenuItems transition className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    <MenuItem>
                      <button
                        onClick={() => setShowReportBuilder(true)}
                        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Generate Report
                      </button>
                    </MenuItem>
                    <MenuItem>
                      <button
                        onClick={() => navigate(`/projects/${projectId}/settings`)}
                        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Project Settings
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            </div> */}
          </div>

          {/* Tab Navigation */}
          <div className="pb-3">
            {/* Mobile Tab Selection */}
            <div className="grid grid-cols-1 sm:hidden">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                aria-label="Select a tab"
                className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
              >
                {['Overview', 'Measurements', 'Analytics', 'Details', 'Reports', 'Export History'].map((tab) => (
                  <option key={tab} value={tab}>
                    {tab}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500"
              />
            </div>

            {/* Desktop Tab Navigation */}
            <div className="hidden sm:block">
              <nav aria-label="Tabs" className="flex space-x-4">
                {['Overview', 'Measurements', 'Analytics', 'Details', 'Reports', 'Export History'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    aria-current={activeTab === tab ? 'page' : undefined}
                    className={classNames(
                      activeTab === tab 
                        ? 'bg-gray-100 text-gray-700' 
                        : 'text-gray-500 hover:text-gray-700',
                      'rounded-md px-3 py-2 text-sm font-medium'
                    )}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="py-3">
        {/* Overview Tab */}
        {activeTab === 'Overview' && (
          <ProjectOverview
            {...projectContext}
          />
        )}

        {/* Measurements Tab */}
        {activeTab === 'Measurements' && (
          <ProjectMeasurements
            {...projectContext}
          />
        )}

        {/* Analytics Tab */}
        {activeTab === 'Analytics' && (
          <ProjectAnalytics />
        )}

        {/* Details Tab */}
        {activeTab === 'Details' && (
          <DetailsTab 
            project={projectData} 
            onUpdateSuccess={fetchProjectData} 
          />
        )}

        {/* Reports Tab */}
        {activeTab === 'Reports' && (
          <DensityReportGenerator
            projectData={{
              project_id: projectId,
              project_display_name: projectData?.name,
              project_name: projectData?.name,
              project_description: projectData?.description,
            }}
            measurements={measurements}
            onGenerateReport={handleGenerateReport}
            isLoading={reportGenerating}
          />
        )}

        {/* Export History Tab */}
        {activeTab === 'Export History' && (
          projectId && user?.id ? (
            <ExportHistory 
              projectId={projectId} 
              userId={user.id}
            />
          ) : (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-700">Loading export history...</span>
            </div>
          )
        )}
      </div>

      {/* Report Builder Modal */}
      {showReportBuilder && (
        <ReportBuilder
          measurements={[]} // This might need to be passed differently
          onClose={() => setShowReportBuilder(false)}
          organizationId={organizationId}
          projectId={projectId}
        />
      )}
    </Layout>
  );
}